import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal } from "react-bootstrap";
import { Frequency, SavedReport } from "./types";
import { toast } from "react-toastify";
import { format } from "date-fns";
import {
  ReportsApi,
  ServerTransferRecurringReportScheduleResponse,
} from "../shared/api/axios-client";
import { createApiService } from "../shared/apiConfig";
import { convert12hToUtc, formatUtcTimeToLocal12h } from "../Data/Date";
import { reportTypes } from "./utils";

export function AddRecurringReportModal({
  show,
  onClose,
  onSave,
  savedReports,
  schedules,
  initialSchedule,
}: {
  show: boolean;
  onClose: () => void;
  onSave: (s: ServerTransferRecurringReportScheduleResponse) => void;
  savedReports: SavedReport[];
  schedules: ServerTransferRecurringReportScheduleResponse[];
  initialSchedule?: ServerTransferRecurringReportScheduleResponse;
}) {
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [frequency, setFrequency] = useState<Frequency>();
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedReportId, setSelectedReportId] = useState<number>();
  const [time, setTime] = useState("");
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [editingEmailValue, setEditingEmailValue] = useState("");
  const isEditing = initialSchedule != null;
  const reportsApi = createApiService(ReportsApi);
  
  const filteredReports = useMemo(() => {
    if (!selectedType) return savedReports;
    const typeEnumValue = reportTypes[selectedType as keyof typeof reportTypes];
    return savedReports.filter((r) => r.type === typeEnumValue);
  }, [selectedType, savedReports]);

  useEffect(() => {
    if (isEditing) {
      setEmails(initialSchedule.emails!);
      setFrequency(initialSchedule.frequency as Frequency);
      setSelectedDays(initialSchedule.days ?? []);
      setSelectedReportId(initialSchedule.savedReportId);
      if (initialSchedule.time) {
        const localTime = formatUtcTimeToLocal12h(initialSchedule.time);
        setTime(localTime);
      } else {
        setTime("");
      }
    } else {
      setEmails([]);
      setFrequency(undefined);
      setSelectedDays([]);
      setSelectedReportId(undefined);
      setTime("");
    }
    setEmailInput("");
  }, [initialSchedule, show]);

  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const date = new Date();
    date.setHours(i, 0, 0, 0);
    return format(date, "h a");
  });

  const handleAddEmail = () => {
    if (!emailInput.trim()) return;
    if (emails.length >= 10) {
      toast.error("You can only add up to 10 emails.");
      return;
    }
    const isValid = isValidEmail(emailInput!);

    if (!isValid) {
      return;
    }

    if (isValid && !emails.includes(emailInput)) {
      setEmails((prev) => [...prev, emailInput]);
      setEmailInput("");
    }
  };

  const handleEditEmailStart = (email: string) => {
    setEditingEmail(email);
    setEditingEmailValue(email);
  };

  const handleEditEmailCancel = () => {
    setEditingEmail(null);
    setEditingEmailValue("");
  };

  const handleEditEmailSave = () => {
    if (!editingEmailValue.trim()) return;

    const isValid = isValidEmail(editingEmailValue);

    if (!isValid) {
      handleEditEmailCancel();
      return;
    }
    setEmails((prev) =>
      prev.map((e) => (e === editingEmail ? editingEmailValue.trim() : e))
    );
    handleEditEmailCancel();
  };

  const isValidEmail = (email: string) => {
    const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(
      email
    );

    if (!isValid) {
      toast.error("Invalid Email.");
      return false;
    }
    return true;
  };

  const handleRemoveEmail = (email: string) =>
    setEmails(emails.filter((e) => e !== email));

  const toggleDay = (day: string) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleSubmit = async () => {
    const selectedReport = savedReports.find((r) => r.id === selectedReportId);
    if (!selectedReport || emails.length === 0) return;

    const alreadyScheduled = schedules.some(
      (s) => s.savedReportId === selectedReportId && s !== initialSchedule
    );
    if (alreadyScheduled) {
      toast.error("This report already has a recurring schedule.");
      return;
    }

    const utcTimeString = convert12hToUtc(time);

    const requestPayload = {
      savedReportId: selectedReportId,
      emails,
      frequency,
      days: selectedDays.length > 0 ? selectedDays : null,
      time: utcTimeString,
    };

    const result = isEditing
      ? await reportsApi.monitorReportsUpdateRecurringReportPost({
          recurringReportScheduleId: initialSchedule.recurringReportScheduleId,
          serverTransferRecurringReportScheduleRequest: requestPayload,
        })
      : await reportsApi.monitorReportsAddRecurringReportPost({
          serverTransferRecurringReportScheduleRequest: requestPayload,
        });

    if (result.status !== 200) {
      toast.error(
        isEditing ? "Failed to edit report." : "Failed to create report."
      );
      return;
    }

    toast.success(
      isEditing
        ? "Recurring report updated successfully."
        : "Recurring report created successfully."
    );

    onSave({
      recurringReportScheduleId: initialSchedule?.recurringReportScheduleId,
      savedReportId: selectedReportId!,
      emails,
      frequency,
      days: frequency === Frequency.Weekly ? selectedDays : undefined,
      time: utcTimeString,
      lastRunTime: undefined,
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? "Edit Recurring Report" : "New Recurring Report"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {savedReports.length === 0 ? (
          <Alert variant="info" className="alert-light-bg">
            <Alert.Heading>No Saved Reports Available</Alert.Heading>
            <p className="mt-3">
              To create a recurring report, you must save a report first. Here's
              how:
              <ul>
                <li>Click on a report tab</li>
                <li>Add filters</li>
                <li>Click "Save Report"</li>
              </ul>
              The report will be saved and you can return here to set up
              recurring delivery.
            </p>
          </Alert>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setSelectedReportId(undefined);
                }}
              >
                <option value="">All types...</option>
                {Object.keys(reportTypes)
                  .filter((key) => isNaN(Number(key)))
                  .map((key) => (
                    <option key={key} value={key}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Saved Report</Form.Label>
              <Form.Select
                value={selectedReportId}
                onChange={(e) => setSelectedReportId(Number(e.target.value))}
              >
                <option value="">Select a report...</option>
                {filteredReports.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Frequency</Form.Label>
              <Form.Select
                value={frequency}
                onChange={(e) =>
                  setFrequency(Number(e.target.value) as Frequency)
                }
              >
                <option value="">Select a Frequency...</option>
                <option value={Frequency.Daily}>Daily</option>
                <option value={Frequency.Weekly}>Weekly</option>
                <option value={Frequency.Monthly}>1st of each Month</option>
              </Form.Select>
            </Form.Group>

            {frequency === Frequency.Weekly && (
              <div className="mb-3">
                <Form.Label>Select Days</Form.Label>
                <div className="d-flex flex-row  flex-nowrap ">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                    (d) => (
                      <Form.Check
                        key={d}
                        inline
                        label={d}
                        type="checkbox"
                        checked={selectedDays.includes(d)}
                        onChange={() => toggleDay(d)}
                      />
                    )
                  )}
                </div>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Send Time</Form.Label>
              <Form.Select
                value={time}
                onChange={(e) => setTime(e.target.value)}
              >
                <option value="">Select a Time...</option>
                {timeOptions.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Emails</Form.Label>
              <div className="d-flex gap-2 flex-wrap mb-2">
                {emails.map((email) =>
                  editingEmail === email ? (
                    <div
                      key={email}
                      className="d-flex align-items-center gap-1"
                    >
                      <Form.Control
                        key={email}
                        size="sm"
                        type="email"
                        value={editingEmailValue}
                        autoFocus
                        onChange={(e) => setEditingEmailValue(e.target.value)}
                        onBlur={handleEditEmailSave}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            handleEditEmailCancel();
                          }
                        }}
                        style={{ width: "auto", minWidth: 150, marginRight: 5 }}
                      />
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={handleEditEmailSave}
                        disabled={!editingEmailValue.trim()}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleEditEmailCancel}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Badge
                      key={email}
                      bg="secondary"
                      className="d-flex align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleEditEmailStart(email)}
                    >
                      {email}
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveEmail(email);
                        }}
                        style={{ marginLeft: 6, cursor: "pointer" }}
                      >
                        ×
                      </span>
                    </Badge>
                  )
                )}
              </div>
              <div className="d-flex gap-2">
                <Form.Control
                  size="sm"
                  type="email"
                  placeholder="Add email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                />
                <Button
                  size="sm"
                  onClick={handleAddEmail}
                  disabled={!emailInput}
                >
                  Add
                </Button>
              </div>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        {savedReports.length > 0 && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={
              !selectedReportId ||
              emails.length === 0 ||
              !isScheduleModified() ||
              frequency == undefined ||
              time == ""
            }
          >
            Save
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );

  function isScheduleModified(): boolean {
    if (!isEditing) return true;

    const selectedReportChanged =
      initialSchedule.savedReportId !== selectedReportId;
    const frequencyChanged = initialSchedule.frequency !== frequency;
    const daysChanged =
      JSON.stringify(initialSchedule.days ?? []) !==
      JSON.stringify(selectedDays);
    const initialLocalTime = formatUtcTimeToLocal12h(initialSchedule.time!);
    const timeChanged = initialLocalTime !== time;
    const emailsChanged =
      JSON.stringify(initialSchedule.emails!.sort()) !==
      JSON.stringify(emails.slice().sort());

    return (
      selectedReportChanged ||
      frequencyChanged ||
      daysChanged ||
      timeChanged ||
      emailsChanged
    );
  }
}
